import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Wrench, Plus, CheckCircle, XCircle, AlertTriangle, Clock,
  Truck, Hammer, Shield, Zap, Construction, Car, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EquipmentLogPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [checklists, setChecklists] = useState({});
  const [stats, setStats] = useState(null);
  const [dueInspections, setDueInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const { toast } = useToast();

  const [equipmentForm, setEquipmentForm] = useState({
    name: '',
    category: 'other',
    location: '',
    manufacturer: '',
    model: '',
    serial_number: ''
  });

  const [inspectionForm, setInspectionForm] = useState({
    equipment_id: '',
    inspection_type: 'pre_use',
    inspector_name: '',
    checklist_items: [],
    overall_result: 'pass',
    notes: '',
    defects_found: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [equipmentRes, inspectionsRes, categoriesRes, checklistsRes, statsRes, dueRes] = await Promise.all([
        axios.get(`${API}/api/equipment-log/equipment`),
        axios.get(`${API}/api/equipment-log/inspections`),
        axios.get(`${API}/api/equipment-log/categories`),
        axios.get(`${API}/api/equipment-log/checklists`),
        axios.get(`${API}/api/equipment-log/stats/summary`),
        axios.get(`${API}/api/equipment-log/due-inspections`)
      ]);
      setEquipment(equipmentRes.data.equipment || []);
      setInspections(inspectionsRes.data.inspections || []);
      setCategories(categoriesRes.data.categories || []);
      setChecklists(checklistsRes.data.checklists || {});
      setStats(statsRes.data);
      setDueInspections(dueRes.data.due_for_inspection || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/equipment-log/equipment`, equipmentForm);
      toast({ title: 'Success', description: 'Equipment registered!' });
      setShowAddEquipment(false);
      setEquipmentForm({ name: '', category: 'other', location: '', manufacturer: '', model: '', serial_number: '' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add equipment', variant: 'destructive' });
    }
  };

  const startInspection = (equip) => {
    setSelectedEquipment(equip);
    const categoryChecklist = checklists[equip.category] || checklists.power_tools || [];
    setInspectionForm({
      equipment_id: equip.id,
      inspection_type: 'pre_use',
      inspector_name: '',
      checklist_items: categoryChecklist.map(item => ({ item, result: 'pass', notes: '' })),
      overall_result: 'pass',
      notes: '',
      defects_found: []
    });
    setShowInspectionForm(true);
  };

  const handleInspectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const hasFailures = inspectionForm.checklist_items.some(item => item.result === 'fail');
      const finalForm = {
        ...inspectionForm,
        overall_result: hasFailures ? 'fail' : inspectionForm.overall_result
      };
      await axios.post(`${API}/api/equipment-log/inspections`, finalForm);
      toast({ title: 'Success', description: 'Inspection logged!' });
      setShowInspectionForm(false);
      setSelectedEquipment(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to log inspection', variant: 'destructive' });
    }
  };

  const updateChecklistItem = (index, field, value) => {
    const updated = [...inspectionForm.checklist_items];
    updated[index] = { ...updated[index], [field]: value };
    setInspectionForm({ ...inspectionForm, checklist_items: updated });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'fail': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'needs_attention': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'pass': return 'bg-green-100 text-green-800';
      case 'fail': return 'bg-red-100 text-red-800';
      case 'needs_attention': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      heavy_equipment: Truck,
      power_tools: Zap,
      hand_tools: Hammer,
      fall_protection: Shield,
      lifting_equipment: Construction,
      vehicles: Car,
      default: Wrench
    };
    const Icon = icons[category] || icons.default;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Equipment Inspection Log</h1>
          <p className="text-gray-600">Track and manage equipment inspections</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowAddEquipment(true)} variant="outline">
            <Plus className="w-4 h-4 mr-2" /> Add Equipment
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Equipment</p>
                <p className="text-2xl font-bold">{stats?.total_equipment || 0}</p>
              </div>
              <Wrench className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inspections</p>
                <p className="text-2xl font-bold">{stats?.total_inspections || 0}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pass Rate</p>
                <p className="text-2xl font-bold text-green-600">{stats?.pass_rate || 0}%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{stats?.failed || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        <Card className={dueInspections.length > 0 ? 'border-orange-300 bg-orange-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Due for Inspection</p>
                <p className="text-2xl font-bold text-orange-600">{dueInspections.length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Due Inspections Alert */}
      {dueInspections.length > 0 && (
        <Card className="border-orange-300 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center text-orange-800">
              <AlertTriangle className="w-5 h-5 mr-2" /> Equipment Due for Inspection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {dueInspections.slice(0, 6).map((equip) => (
                <div key={equip.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center">
                    {getCategoryIcon(equip.category)}
                    <span className="ml-2 font-medium">{equip.name}</span>
                  </div>
                  <Button size="sm" onClick={() => startInspection(equip)}>Inspect</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="equipment" className="w-full">
        <TabsList>
          <TabsTrigger value="equipment">Equipment ({equipment.length})</TabsTrigger>
          <TabsTrigger value="inspections">Recent Inspections ({inspections.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="equipment" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipment.map((equip) => (
              <Card key={equip.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center">
                      {getCategoryIcon(equip.category)}
                      <span className="ml-2">{equip.name}</span>
                    </CardTitle>
                    {getStatusIcon(equip.last_inspection_result)}
                  </div>
                  <CardDescription>{equip.equipment_id}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category:</span>
                      <Badge variant="outline">{equip.category?.replace('_', ' ')}</Badge>
                    </div>
                    {equip.location && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span>{equip.location}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Inspection:</span>
                      <span>{equip.last_inspection_date ? new Date(equip.last_inspection_date).toLocaleDateString() : 'Never'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Inspections:</span>
                      <span>{equip.inspection_count || 0}</span>
                    </div>
                  </div>
                  <Button className="w-full mt-4" size="sm" onClick={() => startInspection(equip)}>
                    <CheckCircle className="w-4 h-4 mr-2" /> Log Inspection
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          {equipment.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Equipment Registered</h3>
                <p className="text-gray-600 mb-4">Add your first piece of equipment to start tracking inspections</p>
                <Button onClick={() => setShowAddEquipment(true)}>Add Equipment</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="inspections" className="space-y-4">
          <div className="space-y-3">
            {inspections.map((inspection) => (
              <Card key={inspection.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(inspection.overall_result)}
                      <div>
                        <p className="font-medium">{inspection.equipment_name}</p>
                        <p className="text-sm text-gray-600">
                          {inspection.inspection_type?.replace('_', ' ')} • {inspection.inspector_name} • {new Date(inspection.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge className={getResultColor(inspection.overall_result)}>
                      {inspection.overall_result?.replace('_', ' ')}
                    </Badge>
                  </div>
                  {inspection.notes && (
                    <p className="text-sm text-gray-600 mt-2 pl-9">{inspection.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {inspections.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Inspections Yet</h3>
                <p className="text-gray-600">Start by inspecting your equipment</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Equipment Dialog */}
      <Dialog open={showAddEquipment} onOpenChange={setShowAddEquipment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Equipment</DialogTitle>
            <DialogDescription>Register new equipment for inspection tracking</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEquipment} className="space-y-4">
            <div>
              <Label>Equipment Name *</Label>
              <Input value={equipmentForm.name} onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})} required />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={equipmentForm.category} onValueChange={(v) => setEquipmentForm({...equipmentForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={equipmentForm.location} onChange={(e) => setEquipmentForm({...equipmentForm, location: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Manufacturer</Label>
                <Input value={equipmentForm.manufacturer} onChange={(e) => setEquipmentForm({...equipmentForm, manufacturer: e.target.value})} />
              </div>
              <div>
                <Label>Model</Label>
                <Input value={equipmentForm.model} onChange={(e) => setEquipmentForm({...equipmentForm, model: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Serial Number</Label>
              <Input value={equipmentForm.serial_number} onChange={(e) => setEquipmentForm({...equipmentForm, serial_number: e.target.value})} />
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Add Equipment</Button>
              <Button type="button" variant="outline" onClick={() => setShowAddEquipment(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inspection Form Dialog */}
      <Dialog open={showInspectionForm} onOpenChange={setShowInspectionForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Equipment Inspection</DialogTitle>
            <DialogDescription>
              {selectedEquipment?.name} - {selectedEquipment?.equipment_id}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInspectionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Inspector Name *</Label>
                <Input value={inspectionForm.inspector_name} onChange={(e) => setInspectionForm({...inspectionForm, inspector_name: e.target.value})} required />
              </div>
              <div>
                <Label>Inspection Type *</Label>
                <Select value={inspectionForm.inspection_type} onValueChange={(v) => setInspectionForm({...inspectionForm, inspection_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pre_use">Pre-Use</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Inspection Checklist</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {inspectionForm.checklist_items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm flex-1">{item.item}</span>
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant={item.result === 'pass' ? 'default' : 'outline'} 
                        className={item.result === 'pass' ? 'bg-green-600' : ''}
                        onClick={() => updateChecklistItem(index, 'result', 'pass')}>
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      <Button type="button" size="sm" variant={item.result === 'fail' ? 'default' : 'outline'}
                        className={item.result === 'fail' ? 'bg-red-600' : ''}
                        onClick={() => updateChecklistItem(index, 'result', 'fail')}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div>
              <Label>Notes</Label>
              <Textarea value={inspectionForm.notes} onChange={(e) => setInspectionForm({...inspectionForm, notes: e.target.value})} placeholder="Any additional notes or observations" />
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
                <CheckCircle className="w-4 h-4 mr-2" /> Submit Inspection
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowInspectionForm(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentLogPage;
